import React from "react";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Forms } from "@/types/form";
import { useFormListener } from "@/hooks/form/useFormListener";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import schemas from "@/utils/schemas";
import { useSettings } from "./hooks";
import { Info } from "lucide-react";
import constants from "@/utils/constant";

interface Props {
  data: Forms["Vendor"];
}

const Settings: React.FC<Props> = ({ data: vendor }) => {
  const form = useForm<Forms["Vendor"]>({
    resolver: zodResolver(schemas.vendor),
    defaultValues: {
      external_id: vendor.external_id ?? "",
      title: vendor.title ?? "",
      company_id: vendor.company_id ?? "",
      email: vendor.email ?? "",
      phone: vendor.phone ?? "",
      iban: vendor.iban ?? "",
      vat_registered: vendor.vat_registered ?? false,
      address: {
        country: vendor.address?.country ?? "",
        city: vendor.address?.city ?? "",
        street: vendor.address?.street ?? "",
        zipCode: vendor.address?.zipCode ?? "",
      },
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;

  useFormListener("form-vendor-settings", isDirty, isSubmitting);

  const { mutateAsync: onSubmit } = useSettings();

  return (
    <Form {...form}>
      <form
        id="form-vendor-settings"
        onSubmit={handleSubmit(async (data) => await onSubmit(data))}
        className="form-container mt-5 flex flex-col gap-5 xl:w-1/2 w-full m-auto"
      >
        <div className="flex items-center gap-2 text-gray-600 text-sm">
          <Info className="text-blue-300" />
          <p className="font-medium">
            To update your details, please contact us.
          </p>
        </div>
        <FormField
          control={control}
          name="external_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>External ID</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  id="external_id"
                  type="text"
                  placeholder="External ID"
                  value={field.value ?? ""}
                  disabled
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  id="title"
                  type="text"
                  placeholder="Title"
                  disabled
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="company_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company ID</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  id="company_id"
                  type="text"
                  placeholder="Company ID"
                  disabled
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  id="email"
                  type="email"
                  placeholder="Email"
                  disabled
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  id="phone"
                  type="text"
                  placeholder="Phone"
                  disabled
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="col-span-1 grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name="iban"
            render={({ field }) => (
              <FormItem>
                <FormLabel>IBAN</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="iban"
                    type="text"
                    placeholder="IBAN"
                    disabled
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="vat_registered"
            render={({ field }) => (
              <FormItem>
                <FormLabel>VAT Registered</FormLabel>
                <FormControl>
                  <Input
                    value={field.value ? "Yes" : "No"}
                    id="vat_registered"
                    type="text"
                    disabled
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={control}
          name="address.country"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Country</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value?.toUpperCase() || ""}
                disabled
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a verified email to display" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {constants.COUNTRY_SELECT_ITEMS.map((i) => (
                    <SelectItem key={i.value} value={i.value}>
                      {i.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="address.city"
          render={({ field }) => (
            <FormItem>
              <FormLabel>City</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  id="city"
                  type="text"
                  placeholder="City"
                  disabled
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="col-span-1 grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name="address.street"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Street</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="street"
                    type="text"
                    placeholder="Street"
                    disabled
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="address.zipCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Postal Code</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="zipCode"
                    type="text"
                    placeholder="Postal Code"
                    disabled
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
};

export default Settings;
